import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useDateRange } from "@/state/DateRangeContext";
import { ReportingAnalyticsDateRange } from "@/pages/analytics/lib/reportingAnalyticsDateRange";
import { botReportingQaApiClient } from "@/api/BotReportingQaApiClient";
import { botQaJobPoller } from "@/pages/analytics/services/BotQaJobPoller";
import { ShareList } from "./ReportingChartBlocks";

const RUN_LIMIT = 50;

/**
 * @param {{
 *   botQa: object | null,
 *   noWorkspace: boolean,
 *   onQaComplete?: () => void,
 * }} props
 */
export default function BotQaSection({ botQa, noWorkspace, onQaComplete }) {
  const { startDate, endDate } = useDateRange();
  const [jobStatus, setJobStatus] = useState(/** @type {string | null} */ (null));
  const [running, setRunning] = useState(false);

  const summary = botQa?.qaSummary;
  const scoreRows = Array.isArray(botQa?.qaScoreRows) ? botQa.qaScoreRows : [];
  const qaEmpty = Boolean(botQa?.qaEmpty);

  const handleRunAnalysis = useCallback(async () => {
    if (noWorkspace || running) return;
    setRunning(true);
    setJobStatus("queued");
    try {
      const days = new ReportingAnalyticsDateRange({ startDate, endDate }).inclusiveDays;
      const accepted = await botReportingQaApiClient.runQaAnalysis({
        limit: RUN_LIMIT,
        days,
      });
      const jobId = accepted?.job_id;
      if (!jobId) {
        throw new Error("No job id returned from server");
      }
      await botQaJobPoller.pollUntilComplete(jobId, {
        onStatus: (s) => setJobStatus(s),
      });
      setJobStatus("completed");
      toast.success("QA analysis completed");
      onQaComplete?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setJobStatus("failed");
      toast.error(msg);
    } finally {
      setRunning(false);
      setTimeout(() => setJobStatus(null), 4000);
    }
  }, [noWorkspace, running, startDate, endDate, onQaComplete]);

  if (!botQa) return null;

  return (
    <section className="mt-10 border-t border-slate-100 pt-8 dark:border-slate-800">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Quality & coaching</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            AI-graded bot conversations in the selected UTC range. Batch run uses the same span as{" "}
            {new ReportingAnalyticsDateRange({ startDate, endDate }).inclusiveDays} day(s) from the date picker.
          </p>
        </div>
        <button
          type="button"
          disabled={noWorkspace || running}
          onClick={handleRunAnalysis}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-violet-500 dark:hover:bg-violet-600"
        >
          {running ? (
            <>
              <i className="ri-loader-4-line animate-spin text-lg" aria-hidden />
              {jobStatus === "running" ? "Analyzing…" : jobStatus === "queued" ? "Queued…" : "Working…"}
            </>
          ) : (
            <>
              <i className="ri-play-circle-line text-lg" aria-hidden />
              Run AI analysis
            </>
          )}
        </button>
      </div>

      {qaEmpty ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
          No QA-graded bot sessions in this range. End bot conversations with messages, then run AI analysis.
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="ui-card">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Analyzed sessions</p>
              <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {summary?.totalConversations ?? 0}
              </p>
            </div>
            <div className="ui-card">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Avg overall score</p>
              <p className="mt-2 text-2xl font-semibold tabular-nums text-violet-700 dark:text-violet-300">
                {Number(summary?.averageOverallScore ?? 0).toFixed(1)}/100
              </p>
            </div>
            <div className="ui-card">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Response times</p>
              <p className="mt-2 text-sm text-slate-800 dark:text-slate-200">
                First: {Number(summary?.firstResponseAvg ?? 0).toFixed(1)}s · Avg:{" "}
                {Number(summary?.avgResponseAvg ?? 0).toFixed(1)}s
              </p>
            </div>
          </div>
          <ShareList
            title="QA score breakdown"
            subtitle={summary?.periodName ? `Period: ${summary.periodName}` : "Average KPI scores (0–100)"}
            rows={scoreRows.map((r) => ({
              label: r.label,
              count: null,
              value: r.value,
              color: r.color,
            }))}
            emptyText="No score breakdown available."
          />
        </>
      )}
    </section>
  );
}
